import { doc, getDoc, updateDoc, setDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateProfile,
} from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import type { MedicoCompleto } from '../types/auth';

// ─── Obtener perfil del médico ──────────────────────────────

export async function getMedicoProfile(
  uid: string,
  email?: string,
): Promise<MedicoCompleto | null> {
  // Primary lookup by UID (document ID)
  const snap = await getDoc(doc(db, 'medicos', uid));
  if (snap.exists()) {
    return { id: snap.id, ...snap.data() } as MedicoCompleto;
  }

  // Fallback: query by email in case document ID doesn't match UID
  if (email) {
    const q = query(
      collection(db, 'medicos'),
      where('email', '==', email.toLowerCase()),
    );
    const results = await getDocs(q);
    if (!results.empty) {
      const docSnap = results.docs[0];
      return { id: docSnap.id, ...docSnap.data() } as MedicoCompleto;
    }
  }

  return null;
}

// ─── Actualizar datos del perfil ────────────────────────────

export interface ProfileUpdateData {
  nombre?: string;
  apellidos?: string;
  telefono?: string;
  especialidad?: string;
  subespecialidad?: string | null;
  centroMedico?: string;
  notificacionesSonido?: boolean;
  fondoVirtual?: string;
  /** Cédula del médico. */
  cedula?: string;
  /** No. de Registro de Idoneidad. */
  colegiadoId?: string;
  /** Código del médico en SEIS. */
  seisMedicoId?: string | null;
  /** Código de la especialidad en SEIS. */
  seisEspecialidadId?: string | null;
  /** Código de la instalación de salud en SEIS. */
  seisInstalacionId?: string | null;
}

export async function updateMedicoProfile(
  uid: string,
  data: ProfileUpdateData,
): Promise<void> {
  const updateData: Record<string, unknown> = {
    ...data,
    actualizadoEn: Timestamp.now(),
  };

  if (data.nombre || data.apellidos) {
    const profile = await getMedicoProfile(uid);
    const nombre = data.nombre ?? profile?.nombre ?? '';
    const apellidos = data.apellidos ?? profile?.apellidos ?? '';
    updateData.nombreCompleto = `${nombre} ${apellidos}`.trim();

    // Update Firebase Auth displayName
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, {
        displayName: updateData.nombreCompleto as string,
      });
    }
  }

  await updateDoc(doc(db, 'medicos', uid), updateData);
}

// ─── Subir avatar ───────────────────────────────────────────

const MAX_AVATAR_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_EXTS = ['jpg', 'jpeg', 'png', 'webp'];

/**
 * Determina la extensión canónica del archivo a partir del MIME type
 * (no del nombre, que puede mentir). Devuelve siempre una de AVATAR_EXTS.
 */
function getExtFromMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg'; // image/jpeg
}

/**
 * Traduce errores de Firebase Storage a mensajes legibles para el usuario.
 */
function translateStorageError(err: unknown): string {
  const e = err as { code?: string; message?: string };
  const code = e.code ?? '';
  if (code === 'storage/unauthorized') {
    return 'No tiene permiso para subir esta imagen. Verifique con el administrador ' +
      'que las reglas de Firebase Storage estén desplegadas (storage.rules).';
  }
  if (code === 'storage/canceled') return 'Subida cancelada.';
  if (code === 'storage/quota-exceeded') return 'El almacenamiento ha alcanzado su cuota.';
  if (code === 'storage/unauthenticated') {
    return 'Sesión expirada. Inicie sesión nuevamente.';
  }
  if (code === 'storage/retry-limit-exceeded') {
    return 'Conexión inestable. Intente nuevamente.';
  }
  return e.message || 'No se pudo subir la imagen.';
}

export async function uploadAvatar(
  uid: string,
  file: File,
): Promise<string> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Formato no soportado. Use JPG, PNG o WebP.');
  }
  if (file.size > MAX_AVATAR_SIZE) {
    throw new Error('La imagen no debe superar 2 MB.');
  }
  if (!auth.currentUser) {
    throw new Error('Sesión expirada. Inicie sesión nuevamente.');
  }
  if (auth.currentUser.uid !== uid) {
    throw new Error('No puede modificar el avatar de otro usuario.');
  }

  const ext = getExtFromMime(file.type);
  const storageRef = ref(storage, `avatars/${uid}/avatar.${ext}`);

  // Best-effort cleanup: borrar avatares previos con OTRA extensión para
  // evitar archivos huérfanos cuando el usuario cambia de PNG a JPG, etc.
  await Promise.all(
    AVATAR_EXTS
      .filter((other) => other !== ext)
      .map(async (other) => {
        try {
          await deleteObject(ref(storage, `avatars/${uid}/avatar.${other}`));
        } catch {
          // Si no existe (object-not-found) o falta permiso, ignoramos
        }
      }),
  );

  try {
    await uploadBytes(storageRef, file, {
      contentType: file.type,
      cacheControl: 'public, max-age=86400',
    });
  } catch (err) {
    throw new Error(translateStorageError(err));
  }

  let downloadUrl: string;
  try {
    downloadUrl = await getDownloadURL(storageRef);
  } catch (err) {
    throw new Error(translateStorageError(err));
  }

  // Update Firestore (best-effort si el doc todavía no existe)
  try {
    await updateDoc(doc(db, 'medicos', uid), {
      avatarUrl: downloadUrl,
      actualizadoEn: Timestamp.now(),
    });
  } catch (err) {
    console.warn('No se pudo actualizar avatarUrl en Firestore:', err);
  }

  if (auth.currentUser) {
    await updateProfile(auth.currentUser, { photoURL: downloadUrl });
  }

  return downloadUrl;
}

export async function deleteAvatar(uid: string): Promise<void> {
  if (!auth.currentUser || auth.currentUser.uid !== uid) {
    throw new Error('Sesión expirada o no autorizado.');
  }

  // Best-effort: borrar el avatar en cualquiera de las extensiones permitidas
  await Promise.all(
    AVATAR_EXTS.map(async (ext) => {
      try {
        await deleteObject(ref(storage, `avatars/${uid}/avatar.${ext}`));
      } catch {
        // File doesn't exist or no permission — continue
      }
    }),
  );

  try {
    await updateDoc(doc(db, 'medicos', uid), {
      avatarUrl: null,
      actualizadoEn: Timestamp.now(),
    });
  } catch (err) {
    console.warn('No se pudo limpiar avatarUrl en Firestore:', err);
  }

  if (auth.currentUser) {
    await updateProfile(auth.currentUser, { photoURL: '' });
  }
}

// ─── Avatar del médico gestionado por un ADMIN ──────────────
// El admin puede establecer/eliminar la foto de OTRO médico (ya activado) desde
// EditarMedicoModal. A diferencia de uploadAvatar/deleteAvatar (self-service),
// estas variantes NO tocan el Firebase Auth del admin (photoURL) — solo escriben
// medicos/{uid}.avatarUrl. La escritura en Storage `avatars/{uid}/` la autoriza
// la regla para admin (storage.rules). El refresco a consultas activas lo hace
// el trigger sincronizarFotoConsultas.

export async function uploadMedicoAvatarAdmin(uid: string, file: File): Promise<string> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Formato no soportado. Use JPG, PNG o WebP.');
  }
  if (file.size > MAX_AVATAR_SIZE) {
    throw new Error('La imagen no debe superar 2 MB.');
  }
  if (!auth.currentUser) {
    throw new Error('Sesión expirada. Inicie sesión nuevamente.');
  }

  const ext = getExtFromMime(file.type);
  const storageRef = ref(storage, `avatars/${uid}/avatar.${ext}`);

  // Limpiar avatares previos con otra extensión (evita huérfanos).
  await Promise.all(
    AVATAR_EXTS
      .filter((other) => other !== ext)
      .map(async (other) => {
        try {
          await deleteObject(ref(storage, `avatars/${uid}/avatar.${other}`));
        } catch {
          /* no existe / sin permiso — ignorar */
        }
      }),
  );

  try {
    await uploadBytes(storageRef, file, {
      contentType: file.type,
      cacheControl: 'public, max-age=86400',
    });
  } catch (err) {
    throw new Error(translateStorageError(err));
  }

  let downloadUrl: string;
  try {
    downloadUrl = await getDownloadURL(storageRef);
  } catch (err) {
    throw new Error(translateStorageError(err));
  }

  await updateDoc(doc(db, 'medicos', uid), {
    avatarUrl: downloadUrl,
    actualizadoEn: Timestamp.now(),
  });

  return downloadUrl;
}

export async function deleteMedicoAvatarAdmin(uid: string): Promise<void> {
  if (!auth.currentUser) {
    throw new Error('Sesión expirada. Inicie sesión nuevamente.');
  }

  await Promise.all(
    AVATAR_EXTS.map(async (ext) => {
      try {
        await deleteObject(ref(storage, `avatars/${uid}/avatar.${ext}`));
      } catch {
        /* no existe / sin permiso — continuar */
      }
    }),
  );

  await updateDoc(doc(db, 'medicos', uid), {
    avatarUrl: null,
    actualizadoEn: Timestamp.now(),
  });
}

// ─── Cambiar contraseña ─────────────────────────────────────

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No hay sesión activa.');
  }

  // Re-authenticate before password change
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);

  await updatePassword(user, newPassword);

  // Update password timestamps in Firestore
  const now = Timestamp.now();
  const expiresAt = Timestamp.fromDate(
    new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // 90 days
  );

  await setDoc(doc(db, 'medicos', user.uid), {
    passwordCambiadoEn: now,
    passwordExpiraEn: expiresAt,
    requiereCambioPassword: false,
    actualizadoEn: now,
  }, { merge: true });
}
